/**
 * Teams API Service
 * Handles team creation, membership, and management
 */

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getAuthHeaders = () => {
  const token = localStorage.getItem('auth_token');
  return {
    'Content-Type': 'application/json',
    'Authorization': token ? `Bearer ${token}` : ''
  };
};

/**
 * Create a new team
 */
export const createTeam = async (teamData) => {
  const response = await fetch(`${API_URL}/api/teams`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(teamData)
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to create team');
  }
  
  return await response.json();
};

/**
 * Fetch user's teams
 */
export const fetchMyTeams = async () => {
  const response = await fetch(`${API_URL}/api/teams`, {
    headers: getAuthHeaders()
  });
  
  if (!response.ok) {
    if (response.status === 401) throw new Error('Unauthorized');
    throw new Error('Failed to fetch teams');
  }
  
  return await response.json();
};

/**
 * Fetch team details
 */
export const fetchTeamDetails = async (teamId) => {
  const response = await fetch(`${API_URL}/api/teams/${teamId}`, {
    headers: getAuthHeaders()
  });
  
  if (!response.ok) {
    if (response.status === 404) throw new Error('Team not found');
    if (response.status === 403) throw new Error('You are not a member of this team');
    throw new Error('Failed to fetch team details');
  }
  
  return await response.json();
};

/**
 * Update team details
 */
export const updateTeam = async (teamId, updates) => {
  const response = await fetch(`${API_URL}/api/teams/${teamId}`, {
    method: 'PATCH',
    headers: getAuthHeaders(),
    body: JSON.stringify(updates)
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to update team');
  }
  
  return await response.json();
};

/**
 * Delete a team
 */
export const deleteTeam = async (teamId) => {
  const response = await fetch(`${API_URL}/api/teams/${teamId}`, {
    method: 'DELETE',
    headers: getAuthHeaders()
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to delete team');
  }
  
  return await response.json();
};

/**
 * Leave a team
 */
export const leaveTeam = async (teamId) => {
  const response = await fetch(`${API_URL}/api/teams/${teamId}/leave`, {
    method: 'POST',
    headers: getAuthHeaders()
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to leave team');
  }
  
  return await response.json();
};

/**
 * Update member role
 */
export const updateMemberRole = async (teamId, memberUserId, role) => {
  const response = await fetch(`${API_URL}/api/teams/${teamId}/members/${memberUserId}/role?role=${role}`, {
    method: 'PATCH',
    headers: getAuthHeaders()
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to update member role');
  }
  
  return await response.json();
};

/**
 * Remove a member from the team
 */
export const removeMember = async (teamId, memberUserId) => {
  const response = await fetch(`${API_URL}/api/teams/${teamId}/members/${memberUserId}`, {
    method: 'DELETE',
    headers: getAuthHeaders()
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to remove member');
  }
  
  return await response.json();
};

// ──────────────────────────────────────────────────────────────
// Team Invitations (T2)
// ──────────────────────────────────────────────────────────────

/**
 * Invite a new member to the team
 */
export const inviteMember = async (teamId, inviteData) => {
  const response = await fetch(`${API_URL}/api/teams/${teamId}/invite`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(inviteData)
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to send invite');
  }
  
  return await response.json();
};

/**
 * Fetch pending invites for a team
 */
export const fetchPendingInvites = async (teamId) => {
  const response = await fetch(`${API_URL}/api/teams/${teamId}/invites`, {
    headers: getAuthHeaders()
  });
  
  if (!response.ok) {
    if (response.status === 403) throw new Error('You do not have permission to view invites');
    throw new Error('Failed to fetch invites');
  }
  
  return await response.json();
};

/**
 * Cancel a pending invite
 */
export const cancelInvite = async (teamId, inviteId) => {
  const response = await fetch(`${API_URL}/api/teams/${teamId}/invites/${inviteId}`, {
    method: 'DELETE',
    headers: getAuthHeaders()
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to cancel invite');
  }
  
  return await response.json();
};

/**
 * Validate an invite token (public - no auth required)
 */
export const validateInvite = async (token) => {
  const response = await fetch(`${API_URL}/api/teams/invite/${token}`);
  
  if (!response.ok) {
    throw new Error('Failed to validate invite');
  }
  
  return await response.json();
};

/**
 * Accept an invite (requires auth)
 */
export const acceptInvite = async (token) => {
  const response = await fetch(`${API_URL}/api/teams/invite/${token}/accept`, {
    method: 'POST',
    headers: getAuthHeaders()
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to accept invite');
  }
  
  return await response.json();
};


// ──────────────────────────────────────────────────────────────
// Team Signals (Founder Dashboard)
// ──────────────────────────────────────────────────────────────

/**
 * Get latest team signals for founder dashboard
 */
export const fetchTeamSignals = async (teamId) => {
  const response = await fetch(`${API_URL}/api/teams/${teamId}/signals`, {
    headers: getAuthHeaders()
  });
  
  if (!response.ok) {
    if (response.status === 400) {
      const error = await response.json();
      throw new Error(error.detail || 'Not enough team members for signal dashboard');
    }
    if (response.status === 403) {
      throw new Error('Signal dashboard is only available to team admins');
    }
    throw new Error('Failed to fetch team signals');
  }
  
  return await response.json();
};

/**
 * Get signal history for trend charts
 */
export const fetchSignalHistory = async (teamId, weeks = 4) => {
  const response = await fetch(`${API_URL}/api/teams/${teamId}/signals/history?weeks=${weeks}`, {
    headers: getAuthHeaders()
  });
  
  if (!response.ok) {
    throw new Error('Failed to fetch signal history');
  }
  
  return await response.json();
};

/**
 * Manually trigger signal generation
 */
export const generateTeamSignals = async (teamId) => {
  const response = await fetch(`${API_URL}/api/teams/${teamId}/signals/generate`, {
    method: 'POST',
    headers: getAuthHeaders()
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to generate signals');
  }
  
  return await response.json();
};
